import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import Header from "../components/Header";
import { questions, sections } from "../data/questions";

const LETTERS = ["A", "B", "C", "D"];
const TOTAL_SECONDS = 20 * 60;
const STORAGE_KEY = "quizTimeLeft";

function formatTime(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function CircularTimer({ timeLeft, total }) {
  const pct = timeLeft / total;
  const r = 38;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct);
  const color = pct > 0.4 ? "#10b981" : pct > 0.15 ? "#f59e0b" : "#ef4444";
  const isUrgent = pct <= 0.15;
  return (
    <div style={{ position: "relative", width: 96, height: 96, flexShrink: 0 }}>
      <svg width="96" height="96" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="48" cy="48" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="5" />
        <circle cx="48" cy="48" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s linear, stroke 1s" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 800, fontSize: "1rem", letterSpacing: "2px", color, animation: isUrgent ? "urgentPulse 1s ease-in-out infinite" : "none", transition: "color 1s" }}>
          {formatTime(timeLeft)}
        </span>
        <span style={{ fontSize: "0.52rem", color: "rgba(255,255,255,0.4)", letterSpacing: "1.5px", fontWeight: 600, textTransform: "uppercase" }}>
          {isUrgent ? "HURRY!" : "left"}
        </span>
      </div>
    </div>
  );
}

export default function Quiz() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [timeLeft, setTimeLeft] = useState(() => {
    // ✅ FIX 1: Restore timer from sessionStorage on refresh
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : TOTAL_SECONDS;
  });
  const [timeUp, setTimeUp] = useState(false);
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);

  const timerRef = useRef(null);
  const answersRef = useRef(answers);
  const studentRef = useRef(null);
  const timeLeftRef = useRef(timeLeft);
  const submittingRef = useRef(false);
  const tabSwitchRef = useRef(0);

  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { timeLeftRef.current = timeLeft; }, [timeLeft]);

  // ── Load student ──────────────────────────────────────────────────────────
  useEffect(() => {
    const stored = sessionStorage.getItem("student");
    if (!stored) { navigate("/"); return; }
    const s = JSON.parse(stored);
    setStudent(s);
    studentRef.current = s;
  }, [navigate]);

  // ── Block back button ─────────────────────────────────────────────────────
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPop = () => window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // ── Block copy/cut/rightclick/shortcuts ───────────────────────────────────
  useEffect(() => {
    const block = (e) => e.preventDefault();
    const blockKey = (e) => {
      if (
        (e.ctrlKey && ["c","x","v","a","u","s","p"].includes(e.key.toLowerCase())) ||
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && ["i","j","c"].includes(e.key.toLowerCase()))
      ) { e.preventDefault(); return false; }
    };
    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("contextmenu", block);
    document.addEventListener("keydown", blockKey);
    return () => {
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("keydown", blockKey);
    };
  }, []);

  // ── Wake Lock ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let wakeLock = null;
    const requestWL = async () => {
      try { if ("wakeLock" in navigator) wakeLock = await navigator.wakeLock.request("screen"); } catch (_) {}
    };
    requestWL();
    const onVis = () => { if (document.visibilityState === "visible") requestWL(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (wakeLock) wakeLock.release().catch(() => {});
    };
  }, []);

  // ── Tab switch warning — only show, do NOT auto-submit ────────────────────
  useEffect(() => {
    const onBlur = () => {
      // ✅ FIX 2: Only warn, never auto-submit on blur/tab switch
      if (submittingRef.current) return;
      tabSwitchRef.current += 1;
      setTabSwitchCount(tabSwitchRef.current);
      setShowTabWarning(true);
    };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, []);

  // ── Submit handler ────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (isAutoSubmit = false) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setShowTabWarning(false);
    clearInterval(timerRef.current);

    // ✅ FIX 3: Clear timer from storage so refresh after submit is clean
    sessionStorage.removeItem(STORAGE_KEY);

    const finalAnswers = answersRef.current;
    const currentStudent = studentRef.current;

    try {
      let autoScore = 0;
      questions.forEach(q => { if (finalAnswers[q.id] === q.correct) autoScore++; });

      await addDoc(collection(db, "submissions"), {
        student: currentStudent,
        answers: finalAnswers,
        autoScore,
        totalQuestions: questions.length,
        adminMark: null,
        timeTaken: TOTAL_SECONDS - timeLeftRef.current,
        autoSubmitted: isAutoSubmit,
        tabSwitchCount: tabSwitchRef.current,
        submittedAt: serverTimestamp(),
        status: "pending_review"
      });

      sessionStorage.setItem("submitted", "true");
      navigate("/submitted");
    } catch (err) {
      console.error("Submission error:", err);
      submittingRef.current = false;
      setSubmitting(false);
      // Restore timer storage since submit failed
      sessionStorage.setItem(STORAGE_KEY, String(timeLeftRef.current));

      if (err.code === "permission-denied") {
        alert(
          "❌ Firebase Permission Denied!\n\n" +
          "Go to Firebase Console → Firestore → Rules and paste:\n\n" +
          "rules_version = '2';\nservice cloud.firestore {\n" +
          "  match /databases/{database}/documents {\n" +
          "    match /submissions/{id} {\n" +
          "      allow create: if true;\n" +
          "      allow read, update: if request.auth != null;\n" +
          "      allow delete: if false;\n" +
          "    }\n  }\n}\n\nThen click Publish and retry."
        );
      } else {
        alert("Submission failed: " + err.message);
      }
    }
  }, [navigate]);

  // ── Countdown — saves time to sessionStorage every second ────────────────
  useEffect(() => {
    if (!student) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        const next = prev - 1;
        // ✅ FIX 4: Persist remaining time so refresh restores it
        sessionStorage.setItem(STORAGE_KEY, String(next));
        if (next <= 0) {
          clearInterval(timerRef.current);
          sessionStorage.removeItem(STORAGE_KEY);
          setTimeUp(true);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [student]);

  // ── Auto-submit only when timer hits zero ─────────────────────────────────
  useEffect(() => {
    if (timeUp && !submittingRef.current) handleSubmit(true);
  }, [timeUp, handleSubmit]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const sectionQs = (key) => questions.filter(q => q.sectionKey === key);
  const answeredCount = Object.keys(answers).length;
  const totalQ = questions.length;
  const progress = (answeredCount / totalQ) * 100;
  const handleSelect = (qId, oi) => setAnswers(prev => ({ ...prev, [qId]: oi }));
  const timerPct = timeLeft / TOTAL_SECONDS;
  const timerColor = timerPct > 0.4 ? "#10b981" : timerPct > 0.15 ? "#f59e0b" : "#ef4444";

  if (!student) return <div className="loader" />;

  const activeSec = sections[currentSection];
  const activeQs = sectionQs(activeSec.key);
  const sectionAnswered = activeQs.filter(q => answers[q.id] !== undefined).length;

  return (
    <div className="page-wrapper" style={{ userSelect: "none", WebkitUserSelect: "none" }}>
      <Header subtitle="Assessment" />

      {/* Sticky bar with circular timer */}
      <div style={{ position: "sticky", top: "65px", zIndex: 50, background: "rgba(10,15,30,0.97)", backdropFilter: "blur(20px)", borderBottom: "1px solid var(--border)", padding: "12px 24px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", display: "flex", alignItems: "center", gap: 16 }}>
          <CircularTimer timeLeft={timeLeft} total={TOTAL_SECONDS} />
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: "0.82rem", color: "var(--muted)", fontWeight: 500 }}>👤 {student.name}</span>
              <span style={{ fontSize: "0.82rem", color: "var(--muted)" }}>{answeredCount}/{totalQ} answered</span>
            </div>
            <div style={{ height: 4, background: "rgba(255,255,255,0.07)", borderRadius: 99, overflow: "hidden", marginBottom: 5 }}>
              <div style={{ height: "100%", width: `${timerPct * 100}%`, background: `linear-gradient(90deg, ${timerColor}66, ${timerColor})`, borderRadius: 99, transition: "width 1s linear, background 1s" }} />
            </div>
            <div style={{ height: 4, background: "rgba(255,255,255,0.07)", borderRadius: 99, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(90deg, #7c3aed, #00d4ff)", borderRadius: 99, transition: "width 0.4s ease" }} />
            </div>
          </div>
        </div>
      </div>

      <div className="quiz-wrapper">
        <div className="stats-row">
          <span className="stat-chip total">📝 {totalQ} Total</span>
          <span className="stat-chip answered">✅ {answeredCount} Answered</span>
          <span className="stat-chip pending">⏳ {totalQ - answeredCount} Pending</span>
        </div>

        <div className="section-tabs">
          {sections.map((sec, idx) => {
            const secQs = sectionQs(sec.key);
            const done = secQs.every(q => answers[q.id] !== undefined);
            return (
              <button key={sec.key} className={`section-tab ${idx === currentSection ? "active" : ""} ${done ? "done" : ""}`} onClick={() => setCurrentSection(idx)}>
                {sec.icon} {sec.label}
                <span style={{ fontSize: "0.72rem", opacity: 0.7 }}>({secQs.filter(q => answers[q.id] !== undefined).length}/{sec.count})</span>
              </button>
            );
          })}
        </div>

        <div className="section-header-box">
          <span className="section-icon">{activeSec.icon}</span>
          <div><h2>{activeSec.label}</h2><span>{sectionAnswered}/{activeSec.count} answered</span></div>
        </div>

        {activeQs.map((q, qIdx) => (
          <div key={q.id} className={`question-card ${answers[q.id] !== undefined ? "answered" : ""}`}>
            <div className="question-num">{activeSec.label} · Question {qIdx + 1}</div>
            <p className="question-text">{q.question}</p>
            <div className="options-grid">
              {q.options.map((opt, oi) => (
                <button key={oi} className={`option-btn ${answers[q.id] === oi ? "selected" : ""}`} onClick={() => handleSelect(q.id, oi)}>
                  <span className="option-letter">{LETTERS[oi]}</span>{opt}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 30 }}>
          <button className="btn-secondary" onClick={() => setCurrentSection(p => Math.max(0, p - 1))} disabled={currentSection === 0}>← Previous</button>
          {currentSection < sections.length - 1 ? (
            <button className="btn-primary" style={{ width: "auto", padding: "12px 32px" }} onClick={() => setCurrentSection(p => p + 1)}>Next Section →</button>
          ) : (
            <button className="btn-primary" style={{ width: "auto", padding: "12px 32px" }} onClick={() => setShowConfirm(true)} disabled={submitting}>
              {submitting ? "Submitting..." : `Submit (${answeredCount}/${totalQ})`}
            </button>
          )}
        </div>
        {answeredCount < totalQ && currentSection === sections.length - 1 && (
          <p style={{ textAlign: "center", color: "var(--gold)", fontSize: "0.82rem", marginTop: 12 }}>⚠ {totalQ - answeredCount} question(s) unanswered. You can still submit.</p>
        )}
      </div>

      {/* ── Tab switch warning banner — just a warning, NO auto-submit ── */}
      {showTabWarning && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "rgba(239,68,68,0.97)", border: "1px solid rgba(239,68,68,0.6)",
          borderRadius: 14, padding: "14px 24px", zIndex: 9000,
          display: "flex", alignItems: "center", gap: 12,
          boxShadow: "0 8px 30px rgba(239,68,68,0.5)",
          animation: "slideUp 0.3s ease", minWidth: 320
        }}>
          <span style={{ fontSize: "1.4rem" }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}>Tab switch detected!</div>
            <div style={{ fontSize: "0.78rem", opacity: 0.85 }}>This has been recorded. ({tabSwitchCount} time{tabSwitchCount > 1 ? "s" : ""})</div>
          </div>
          <button onClick={() => setShowTabWarning(false)} style={{ background: "none", border: "none", color: "white", fontSize: "1.4rem", cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* ── Confirm submit modal ── */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 420, textAlign: "center" }}>
            <div style={{ fontSize: "3rem", marginBottom: 16 }}>📤</div>
            <h3>Submit Assessment?</h3>
            <p style={{ color: "var(--muted)", marginBottom: 8, fontSize: "0.9rem" }}>
              You answered <strong style={{ color: "var(--accent)" }}>{answeredCount}</strong> / <strong>{totalQ}</strong> questions.
            </p>
            <p style={{ color: timerColor, fontSize: "0.9rem", fontWeight: 700, marginBottom: 8 }}>⏱ Time remaining: {formatTime(timeLeft)}</p>
            {answeredCount < totalQ && (
              <p style={{ color: "var(--gold)", fontSize: "0.82rem", marginBottom: 8 }}>{totalQ - answeredCount} question(s) will be left blank.</p>
            )}
            <p style={{ color: "var(--muted)", fontSize: "0.8rem", marginBottom: 24 }}>Once submitted, answers cannot be changed.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowConfirm(false)}>Go Back</button>
              <button className="btn-primary" style={{ flex: 1 }} onClick={() => { setShowConfirm(false); handleSubmit(false); }} disabled={submitting}>
                {submitting ? "Submitting..." : "Yes, Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Time's up overlay ── */}
      {timeUp && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-box" style={{ maxWidth: 360, textAlign: "center" }}>
            <div style={{ fontSize: "4rem", marginBottom: 12 }}>⏰</div>
            <h3 style={{ color: "var(--danger)", marginBottom: 8 }}>Time's Up!</h3>
            <p style={{ color: "var(--muted)", fontSize: "0.9rem", marginBottom: 20 }}>Submitting your answers automatically...</p>
            <div className="loader" style={{ margin: "0 auto" }} />
          </div>
        </div>
      )}

      <style>{`
        @keyframes urgentPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes slideUp {
          from { transform: translateX(-50%) translateY(20px); opacity: 0; }
          to   { transform: translateX(-50%) translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}